import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { ChevronRight, QrCode } from 'lucide-react';
import { SlideTransition } from '../../../../components/Transition/Transition';
import { coverQuizPartsDone, coverResultOf, useQuizInvitations } from '../../../CoverQuiz/coverQuizStorage';
import { careerPartTwoOf, careerResultOf } from '../recruitQuizStorage';
import type { IntlLike } from '../clientPresentation';
import type { ClientQuizList } from '../../../../store/clientQuiz/api';
import './RecruitQuizDialog.scss';
import './QuizChooserDialog.scss';

/**
 * 發送測驗 — the drawer's quiz tile asks which PART of this mode's quiz to send
 * before opening its dialog:
 *  - 業務: 保障小測驗 第一部分 (the whole quiz) or 第二部分 (理財目標) on its own;
 *  - 招募: 職涯天賦測驗 第一部分, or 第二部分 (理想事業藍圖) on its own, for
 *    candidates who are serious about joining.
 * Each card says whether this person has done that part; once part 1 is done
 * and part 2 is not, part 2 is marked 建議下一步. A part not done but with a
 * live (unexpired) link out says 已發送連結 instead of 待作答.
 */

export type QuizPartId = 'cover1' | 'cover2' | 'career1' | 'career2';
export type QuizChooserMode = 'business' | 'recruit';

export interface QuizChooserDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (part: QuizPartId) => void;
  clientId: string;
  clientName?: string;
  intl: IntlLike;
  mode: QuizChooserMode;
}

type PartCard = { id: QuizPartId; icon: string; tone: string; done: boolean; sent?: boolean; comingSoon?: boolean };

const livePartsOf = (list: ClientQuizList | null) =>
  new Set((list?.pending || []).filter(q => !q.expired).map(q => q.part));

const QuizChooserDialog = (props: QuizChooserDialogProps) => {
  const { open, onClose, onPick, clientId, clientName, intl, mode } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  // Each mode reads only its own quiz, and only while open, so opening the chooser costs one request.
  const coverClientId = mode === 'business' ? clientId : null;
  const careerClientId = mode === 'recruit' ? clientId : null;
  const { list: coverList } = useQuizInvitations(coverClientId, 'cover', { load: open });
  const { list: careerList } = useQuizInvitations(careerClientId, 'career', { load: open });
  const cover = coverQuizPartsDone(coverResultOf(coverClientId, coverList?.completed));
  const careerDone = Boolean(careerResultOf(careerClientId, careerList?.completed));
  const careerTwoDone = Boolean(careerPartTwoOf(careerList?.completed));
  const coverSent = livePartsOf(coverList);
  const careerSent = livePartsOf(careerList);

  const parts: PartCard[] =
    mode === 'recruit'
      ? [
          { id: 'career1', icon: '🧭', tone: 'violet', done: careerDone, sent: careerSent.has(1) },
          { id: 'career2', icon: '🤝', tone: 'green', done: careerTwoDone, sent: careerSent.has(2) }
        ]
      : [
          { id: 'cover1', icon: '🛡️', tone: 'blue', done: cover.one, sent: coverSent.has(1) },
          { id: 'cover2', icon: '🎯', tone: 'green', done: cover.two, sent: coverSent.has(2) }
        ];
  const nextStep = parts[0].done && !parts[1].done && !parts[1].comingSoon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      className="clientv2-recruit-quiz-modal quiz-chooser-modal"
      data-testid="quiz-chooser"
      data-mode={mode}
      slots={{ transition: SlideTransition }}
      slotProps={{ paper: { style: { borderRadius: 20, background: '#ffffff' } } }}
    >
      <DialogTitle className="rqd-title">
        <div className="rqd-title-row">
          <div className="rqd-title-left">
            <span className="rqd-title-icon">
              <QrCode size={22} />
            </span>
            <strong className="rqd-title-text">{t(`clientv2-quiz-chooser-${mode}-title`)}</strong>
          </div>
        </div>
        <p className="rqd-subtitle">{t('clientv2-quiz-chooser-subtitle', { name: clientName || '' })}</p>
      </DialogTitle>

      <DialogContent className="quiz-chooser-content">
        {parts.map(({ id, icon, tone, done, sent, comingSoon }, index) => (
          <button
            key={id}
            type="button"
            className={`quiz-chooser-card is-${tone}${comingSoon ? ' is-soon' : ''}`}
            data-testid="quiz-chooser-option"
            data-part={id}
            disabled={comingSoon}
            onClick={() => onPick(id)}
          >
            <span className="quiz-chooser-icon" aria-hidden="true">
              {icon}
            </span>
            <span className="quiz-chooser-copy">
              <span className="quiz-chooser-name">
                <strong>{t(`clientv2-quiz-${id}-name`)}</strong>
                {comingSoon && <span className="quiz-chooser-tag is-soon">{t('clientv2-quiz-coming-soon')}</span>}
                {nextStep && index === 1 && <span className="quiz-chooser-tag">{t('clientv2-quiz-next-step')}</span>}
              </span>
              <small className="quiz-chooser-for">{t(`clientv2-quiz-${id}-time`)}</small>
              <span className="quiz-chooser-desc">{t(`clientv2-quiz-${id}-desc`)}</span>
              {!comingSoon && (
                <span className={`quiz-chooser-status${done ? ' is-done' : ''}`} data-testid="quiz-chooser-status">
                  {t(done ? 'clientv2-quiz-done' : sent ? 'clientv2-quiz-sent' : 'clientv2-quiz-pending')}
                </span>
              )}
            </span>
            {!comingSoon && <ChevronRight size={18} className="quiz-chooser-chevron" aria-hidden="true" />}
          </button>
        ))}
      </DialogContent>

      <DialogActions className="rqd-actions">
        <div />
        <Button onClick={onClose} color="primary" variant="outlined" style={{ borderRadius: 8 }}>
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuizChooserDialog;
